import { ROWS_PER_PAGE_OPTIONS } from 'components-ui'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useClientBookingInvoices } from 'service-api/src/hooks/use_client_booking_invoices'
import type { ClientInvoicesByPeriodSearchOptions } from 'service-api/src/types/api'
import { TCustomInvoiceDto } from 'service-api/src/types/general'

export type BookingInvoiceView = 'list' | 'calendar'
export type BookingInvoiceFilters = Pick<
  ClientInvoicesByPeriodSearchOptions,
  'invoiceSubStatus' | 'locationServiceType.serviceTypeId'
>
export interface BookingInvoiceRange {
  start: string
  end: string
}

interface Sort {
  sortColumn: string
  sortOrder: 'asc' | 'desc'
}

export interface UseBookingInvoicesOptions {
  view: BookingInvoiceView
  filters?: BookingInvoiceFilters
  range?: BookingInvoiceRange
}

const DEFAULT_SORT: Sort = { sortColumn: 'updatedAt', sortOrder: 'desc' }
const DEFAULT_LIMIT = ROWS_PER_PAGE_OPTIONS[0]
const NO_FILTERS: BookingInvoiceFilters = {}

const INCLUDES = ['order', 'requestPeriods', 'items.petRelations.invoicePet.locationPetProfile']

const getSearchOptions = (options: ClientInvoicesByPeriodSearchOptions): ClientInvoicesByPeriodSearchOptions => ({
  ...options,
  includes: INCLUDES,
})

/**
 * Accumulates invoices for a view.
 * In list view, filter or sort changes start a fresh list.
 * In calendar view, date range changes merge results into the existing list deduped by id.
 */
export const useBookingInvoices = (
  accountName: string,
  { view, filters = NO_FILTERS, range }: UseBookingInvoicesOptions
) => {
  const [sort, setSort] = useState<Sort>(DEFAULT_SORT)
  const [limit, setLimit] = useState(DEFAULT_LIMIT)
  const [pager, setPager] = useState<{ scope?: string; nextToken?: string; requested?: string }>({})
  const [records, setRecords] = useState<{ key?: string; byId: Map<string, TCustomInvoiceDto> }>({ byId: new Map() })

  const [trackedView, setTrackedView] = useState(view)
  const activeRange = view === 'calendar' ? range : undefined

  // Reset sort and page size when the view changes
  if (trackedView !== view) {
    setTrackedView(view)
    setSort(DEFAULT_SORT)
    setLimit(DEFAULT_LIMIT)
  }

  // Cursor/pagination helpers
  const listKey = useMemo(() => JSON.stringify({ view, filters, sort }), [view, filters, sort])
  const scope = useMemo(() => JSON.stringify({ listKey, range: activeRange }), [listKey, activeRange])
  const requested = pager.scope === scope ? pager.requested : undefined

  const requestOptions: ClientInvoicesByPeriodSearchOptions = useMemo(
    () => ({
      sortOrder: sort.sortOrder,
      ...filters,
      ...(activeRange ? { startDate: `gte_${activeRange.start}`, endDate: `lte_${activeRange.end}` } : {}),
      limit,
      nextToken: requested,
    }),
    [sort.sortOrder, limit, filters, requested, activeRange]
  )

  const searchOptions = useMemo(() => getSearchOptions(requestOptions), [requestOptions])
  const fetchKey = useMemo(() => JSON.stringify([listKey, requestOptions]), [listKey, requestOptions])

  const { data, isLoading } = useClientBookingInvoices(accountName, searchOptions)

  // react-query returns a new data object on every render, so each fetch's result is merged once.
  const mergedKeyRef = useRef<string>()
  useEffect(() => {
    if (!data?.results || mergedKeyRef.current === fetchKey) return
    mergedKeyRef.current = fetchKey
    const nextToken = data.nextToken ?? undefined

    setRecords((prev) => {
      const byId = new Map(prev.key === listKey ? prev.byId : undefined)
      data.results.forEach((invoice) => {
        if (invoice.id) byId.set(invoice.id, invoice)
      })
      return { key: listKey, byId }
    })

    // Calendar view follows nextToken until it is undefined,
    // list view waits for loadMore() to be called manually
    setPager({
      scope,
      nextToken,
      requested: view === 'calendar' && nextToken ? nextToken : requested,
    })
  }, [data, fetchKey, listKey, scope, view, requested])

  const invoices = useMemo(() => (records.key === listKey ? Array.from(records.byId.values()) : []), [records, listKey])

  const hasMore = pager.scope === scope && !!pager.nextToken

  const loadMore = useCallback(() => {
    setPager((prev) => (prev.scope === scope && prev.nextToken ? { ...prev, requested: prev.nextToken } : prev))
  }, [scope])

  const onSort = useCallback((sortColumn: string, sortOrder: 'asc' | 'desc', nextLimit: number) => {
    setSort({ sortColumn, sortOrder })
    setLimit(nextLimit)
  }, [])

  const onRowsPerPageChange = useCallback((nextLimit: number) => setLimit(nextLimit), [])

  return useMemo(
    () => ({ invoices, isLoading, hasMore, loadMore, onSort, onRowsPerPageChange }),
    [invoices, isLoading, hasMore, loadMore, onSort, onRowsPerPageChange]
  )
}
